import {
  useChoices,
  useConfigMapJson,
  useConsumerProfile,
  useCreateDevice,
  useGenerateMeterNumber,
  useGetManufacturers,
  useGetMeterModel,
  useMeterDetail,
  useMeterSearchPrompt,
  usePatchDeviceManagement,
  useSearchMasterMeters,
  useSearchSubmeters,
  useServiceOrdersJobCard,
  useUtilityServices,
} from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { logEvent } from "@shared/analytics/analytics";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { DynamicForm } from "@shared/components/DynamicForm";
import { useToast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { convertToSnakeCase } from "@shared/utils/caseConverter";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle,
  Cpu,
  Hash,
  MapPin,
  Search,
  UserCheck,
  X,
} from "lucide-react";
import { Input } from "@shared/ui/input";
import { SearchInput } from "@shared/components/SearchInput";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";

// Types for better type safety
interface FieldState {
  subCategory: string | null;
  premise: string | null;
  utility: string | null;
  manufacturer: string | null;
  consumerSearch: string;
}

interface DropdownOptions {
  meterTypes: Array<{ value: string | number; label: string }>;
  categoryConfig: Array<{ value: string | number; label: string }>;
  subCategoryConfig: Array<{ value: string | number; label: string }>;
  territoryConfig: Array<{ value: string | number; label: string }>;
  consumersList: Array<{ value: string | number; label: string }>;
  meterRoleTypes: Array<{ value: string | number; label: string }>;
  readSourceOptions: Array<{ value: string | number; label: string }>;
  communicationTypeOptions: Array<{ value: string | number; label: string }>;
  meterOwnershipOptions: Array<{ value: string | number; label: string }>;
}

interface MasterMeterSearchResult {
  id: number;
  label: string;
  meterNumber: string;
  deviceNo: string;
  status: string;
  premiseName: string;
}

const AddEditMeter: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditing = Boolean(id) && id !== "new";
  const remoteUtilityId = useRemoteUtilityId();

  // ===============================
  // STATE MANAGEMENT
  // ===============================
  const [fieldState, setFieldState] = useState<FieldState>({
    subCategory: null,
    premise: null,
    utility: null,
    manufacturer: null,
    consumerSearch: "",
  });

  const [assignmentStatus, setAssignmentStatus] = useState<boolean>(false);
  const [meterType, setMeterType] = useState<string>("");
  const [meterManufacturer, setMeterManufacturer] = useState<string>("");
  const [displayMeterNo, setDisplayMeterNo] = useState<string>("");
  const [searchConsumer, setSearchConsumer] = useState<string>("");
  const [selectedSoCard, setSelectedSoCard] = useState<any>(null);
  const [showConsumerDropdown, setShowConsumerDropdown] =
    useState<boolean>(false);
  const [subMeterSearch, setSubMeterSearch] = useState<string>("");
  const [showSubMeterDropdown, setShowSubMeterDropdown] =
    useState<boolean>(false);
  const [selectedSubMeters, setSelectedSubMeters] = useState<
    Array<{ meterNo: string; deviceNo: string }>
  >([]);

  console.log("selectedSubMeters", selectedSubMeters);
  const [selectedMasterMeterObj, setSelectedMasterMeterObj] =
    useState<MasterMeterSearchResult | null>(null);
  const [searchMasterMeter, setSearchMasterMeter] = useState<string>("");
  const [isEditDataLoaded, setIsEditDataLoaded] = useState<boolean>(false);
  const { toast } = useToast();
  const masterMeterSearchRef = React.useRef<{
    clearSearch: () => void;
    setSearchTerm: (term: string) => void;
  } | null>(null);

  // ===============================
  // API HOOKS
  // ===============================
  const { data: meterNo, isLoading: meterNoLoading } =
    useGenerateMeterNumber(remoteUtilityId);
  const { data: utilityServices } = useUtilityServices(remoteUtilityId);
  const { data: subMeters } = useSearchSubmeters(remoteUtilityId);
  const { data: serviceOrdersJobCard } =
    useServiceOrdersJobCard(remoteUtilityId);
  const { mutate: createDevice } = useCreateDevice();
  const { data: manufacturers } = useGetManufacturers();
  const { data: meterTypes } = useChoices({ choice: "meter_type" });
  const { data: meterRoleTypes } = useChoices({ choice: "meter_role_type" });
  const { data: readSourceOptions } = useChoices({ choice: "ReadSource" });
  const { data: communicationTypeOptions } = useChoices({
    choice: "CommunicationType",
  });
  const { data: meterOwnershipOptions } = useChoices({
    choice: "MeterOwnership",
  });

  const { data: masterMeters } = useSearchMasterMeters(
    remoteUtilityId,
    searchMasterMeter,
  );

  const handleMasterMeterSearch = useCallback(
    async (term: string): Promise<MasterMeterSearchResult[]> => {
      if (term.length < 3) {
        return [];
      }

      // Avoid search trigger if the term is an exact match of an already retrieved master meter's label (e.g. after selection)
      const exactMatch = masterMeters?.find(
        (mm: { meterNumber: string; deviceNo: string }) =>
          `${mm.meterNumber} · ${mm.deviceNo}` === term,
      );
      if (exactMatch) {
        return (
          masterMeters?.map(
            (mm: {
              id: number;
              meterNumber: string;
              deviceNo: string;
              status: string;
              premiseName: string;
            }) => ({
              id: mm.id,
              label: `${mm.meterNumber} · ${mm.deviceNo}`,
              meterNumber: mm.meterNumber,
              deviceNo: mm.deviceNo,
              status: mm.status,
              premiseName: mm.premiseName,
            }),
          ) || []
        );
      }

      setSearchMasterMeter(term);

      const results =
        masterMeters?.map(
          (mm: {
            id: number;
            meterNumber: string;
            deviceNo: string;
            status: string;
            premiseName: string;
          }) => ({
            id: mm.id,
            label: `${mm.meterNumber} · ${mm.deviceNo}`,
            meterNumber: mm.meterNumber,
            deviceNo: mm.deviceNo,
            status: mm.status,
            premiseName: mm.premiseName,
          }),
        ) || [];

      return results;
    },
    [masterMeters],
  );

  const renderMasterMeterResult = useCallback(
    (result: MasterMeterSearchResult) => (
      <div className="p-3 hover:bg-blue-50 cursor-pointer rounded border-l-4 border-transparent hover:border-blue-400 transition-all text-left">
        <div className="font-medium text-gray-900">
          Meter: {result.meterNumber}
        </div>
        <div className="text-sm text-gray-600">
          Device No: {result.deviceNo}
        </div>
        <div className="text-xs text-gray-500 flex justify-between mt-1">
          <span>Premise: {result.premiseName || "N/A"}</span>
          <span className="font-semibold text-blue-600">{result.status}</span>
        </div>
      </div>
    ),
    [],
  );

  // Get edit data first to extract required parameters
  const { data: editData } = useMeterDetail(Number(id), remoteUtilityId);
  console.log("Edit Data of meter:", editData);
  const parentMeterId =
    isEditing && editData?.parentMeter ? Number(editData.parentMeter) : 0;
  const { data: parentMeterDetail } = useMeterDetail(
    parentMeterId,
    remoteUtilityId,
    !!parentMeterId,
  );

  // Config data hooks - Initialize with edit data if available
  const { data: categoryConfig } = useConfigMapJson(
    "category_json",
    remoteUtilityId,
    "category",
  );
  const { data: territoryConfig } = useConfigMapJson(
    "territory_json",
    remoteUtilityId,
    "premise",
  );
  const { data: subCategoryConfig } = useConfigMapJson(
    "category_json",
    remoteUtilityId,
    "sub_category",
    fieldState.subCategory || (isEditing && editData?.category) || null,
  );

  // Consumer and meter data hooks - Initialize with edit data if available
  const { data: consumersList } = useMeterSearchPrompt({
    remoteUtilityId,
    searchData: searchConsumer || (isEditing && editData?.consumerNo) || "",
    productCode:
      fieldState.utility || (isEditing && editData?.productCode) || "",
    premise: fieldState.premise || (isEditing && editData?.premise) || "",
  });

  const { data: meterModels } = useGetMeterModel(
    meterType || (isEditing && editData?.meterType?.toString()) || "",
    meterManufacturer || (isEditing && editData?.meterMake?.toString()) || "",
  );

  const { data: profile } = useConsumerProfile(
    remoteUtilityId,
    fieldState.consumerSearch || (isEditing && editData?.consumerNo) || "",
  );

  const patchDeviceMutation = usePatchDeviceManagement(Number(id));

  // ===============================
  // MEMOIZED OPTIONS
  // ===============================
  const utilityOptions = useMemo(() => {
    if (!utilityServices || !Array.isArray(utilityServices)) return [];
    return utilityServices
      .filter((utility) => utility.isActive === true)
      .map((utility) => ({
        value: utility.name,
        label: utility.name,
      }));
  }, [utilityServices]);

  const manufacturerOptions = useMemo(() => {
    if (!manufacturers || !Array.isArray(manufacturers)) return [];
    return manufacturers.map((model) => ({
      value: model.id,
      label: model.name,
    }));
  }, [manufacturers]);

  const meterModelOptions = useMemo(() => {
    if (!meterModels || !Array.isArray(meterModels)) return [];
    return meterModels.map((model) => ({
      value: model.id,
      label: model.meterModel,
    }));
  }, [meterModels]);

  const jobCardOptions = useMemo(() => {
    if (!serviceOrdersJobCard || !Array.isArray(serviceOrdersJobCard))
      return [];
    return serviceOrdersJobCard.map((jobCard) => ({
      value: jobCard.soId,
      label: jobCard.soName,
    }));
  }, [serviceOrdersJobCard]);

  const dropdownOptions: DropdownOptions = useMemo(() => {
    let consumerOptions = mapToLabelValue(consumersList, "value", "value");

    // Add existing consumer to options when editing
    if (isEditing && editData?.consumerNo) {
      const existingConsumer = consumerOptions.find(
        (option) => option.value === editData.consumerNo,
      );
      if (!existingConsumer) {
        consumerOptions = [
          { value: editData.consumerNo, label: editData.consumerNo },
          ...consumerOptions,
        ];
      }
    }

    return {
      meterTypes: mapToLabelValue(meterTypes),
      categoryConfig: mapToLabelValue(categoryConfig, "name", "code"),
      subCategoryConfig: mapToLabelValue(subCategoryConfig, "name", "code"),
      territoryConfig: mapToLabelValue(territoryConfig, "name", "code"),
      meterRoleTypes: mapToLabelValue(meterRoleTypes),
      consumersList: consumerOptions,
      readSourceOptions: mapToLabelValue(readSourceOptions),
      communicationTypeOptions: mapToLabelValue(communicationTypeOptions),
      meterOwnershipOptions: mapToLabelValue(meterOwnershipOptions),
    };
  }, [
    meterTypes,
    meterRoleTypes,
    categoryConfig,
    communicationTypeOptions,
    meterOwnershipOptions,
    subCategoryConfig,
    territoryConfig,
    consumersList,
    readSourceOptions,
    isEditing,
    editData,
  ]);

  const fields: FormField[] = [
    // Basic Information Section
    {
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      groupOrder: 1,
      name: "device_no",
      type: "text",
      label: "Device Number",
      required: true,
      disabled: !!isEditing,
      placeholder: "Enter meter device number",
      size: "medium",
      variant: "outlined",
      fullWidth: true,
      helperText: "Unique identifier for the meter device",
      className: "col-span-2",
    },
    {
      name: "product_code",
      type: "select",
      label: "Utility Service",
      required: true,
      disabled: !!isEditing,
      placeholder: "Select utility service",
      options: utilityOptions,
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      fullWidth: true,
      clearable: true,
      searchable: true,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Choose the utility service assigned to this meter",
    },
    {
      name: "meter_type",
      type: "select",
      label: "Meter Type",
      placeholder: "Select meter type",
      disabled: !!isEditing,
      required: true,
      options: dropdownOptions?.meterTypes,
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      fullWidth: true,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Select the type of the meter (e.g., digital, analog)",
    },
    {
      name: "meter_classification",
      type: "select",
      label: "Meter Classification",
      placeholder: "Select meter classification",
      required: true,
      options: dropdownOptions?.meterOwnershipOptions,
      className: "col-span-2",
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      helperText:
        "Choose how this meter is categorized within the system (Individual, Master, or Sub-meter).",
    },
    {
      name: "master_meter_role_type",
      type: "select",
      label: "Master Meter Role Type",
      placeholder: "Select master meter role type",
      required: true,
      options: dropdownOptions?.meterRoleTypes,
      className: "col-span-2",
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      showWhen: (formValues) => formValues.meter_classification === "0",
      helperText:
        "Choose how this meter is categorized within the system (Individual, Master, or Sub-meter).",
    },
    {
      name: "link_sub_meters",
      type: "multi-select",
      label: "Link Sub Meter",
      placeholder: "Search and link sub meters",
      required: true,
      className: "col-span-2",
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      showWhen: (formValues) => formValues.meter_classification === "0",
      helperText:
        "Search and select one or more sub-meters to link to this master meter.",

      customRender: (field, form, error) => {
        const selectedIds: number[] = form.watch("link_sub_meters") || [];

        return (
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search sub-meter by meter no or device no..."
                value={subMeterSearch}
                className="pl-9"
                onChange={(e) => {
                  setSubMeterSearch(e.target.value);
                  setShowSubMeterDropdown(true);
                }}
                onFocus={() => setShowSubMeterDropdown(true)}
                onBlur={() =>
                  setTimeout(() => setShowSubMeterDropdown(false), 200)
                }
              />
            </div>

            {/* Dropdown */}
            {showSubMeterDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
                {subMeters?.results
                  ?.filter(
                    (sm: any) =>
                      sm.meterNumber
                        ?.toLowerCase()
                        .includes(subMeterSearch.toLowerCase()) ||
                      sm.deviceNo
                        ?.toLowerCase()
                        .includes(subMeterSearch.toLowerCase()),
                  )
                  .map((sm: any) => {
                    const isSelected = selectedIds.includes(sm.id);
                    return (
                      <button
                        key={sm.id}
                        type="button"
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 flex items-center justify-between transition-colors ${isSelected ? "bg-blue-50" : ""}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const current: number[] =
                            form.getValues("link_sub_meters") || [];
                          const updated = isSelected
                            ? current.filter((id) => id !== sm.id)
                            : [...current, sm.id];
                          form.setValue("link_sub_meters", updated);

                          // ✅ Fixed: correctly add/remove from array
                          setSelectedSubMeters((prev) =>
                            isSelected
                              ? prev.filter((s) => s.deviceNo !== sm.deviceNo)
                              : [
                                  ...prev,
                                  {
                                    meterNo: sm.meterNumber,
                                    deviceNo: sm.deviceNo,
                                  },
                                ],
                          );
                        }}
                      >
                        <div>
                          <p className="font-medium text-gray-800">
                            {sm.meterNumber}
                          </p>
                          <p className="text-xs text-gray-500">
                            Device: {sm.deviceNo}
                          </p>
                        </div>
                        {isSelected && (
                          <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
                        )}
                      </button>
                    );
                  })}
                {!subMeters?.results?.length && (
                  <p className="px-3 py-2 text-sm text-gray-400">
                    No sub-meters found
                  </p>
                )}
              </div>
            )}

            {/* Selected tags - show first 3, then +N selected */}
            {selectedIds.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedSubMeters.slice(0, 3).map((sm) => (
                  <div
                    key={sm.deviceNo}
                    className="flex items-center gap-2 px-2 py-1 bg-gray-100 border border-gray-200 rounded-md text-xs"
                  >
                    <div>
                      <span className="font-medium text-gray-700">
                        {sm.meterNo}
                      </span>
                      <span className="text-gray-400 ml-1">
                        · {sm.deviceNo}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      onClick={() => {
                        const updated = selectedIds.filter(
                          (id) =>
                            subMeters?.find(
                              (s: any) => s.deviceNo === sm.deviceNo,
                            )?.id !== id,
                        );
                        form.setValue("link_sub_meters", updated);
                        setSelectedSubMeters((prev) =>
                          prev.filter((s) => s.deviceNo !== sm.deviceNo),
                        );
                      }}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}

                {/* +N badge when more than 3 selected */}
                {selectedSubMeters.length > 3 && (
                  <div className="flex items-center px-2 py-1 bg-blue-50 border border-blue-200 rounded-md text-xs font-medium text-blue-600">
                    +{selectedSubMeters.length - 3} selected
                  </div>
                )}
              </div>
            )}
          </div>
        );
      },
    },
    {
      name: "linked_master_meter",
      type: "select",
      label: "Select Master Meter",
      placeholder: "Search and select master meter",
      required: false,
      className: "col-span-4",
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      showWhen: (formValues) => formValues.meter_classification === "2",
      helperText: "Search and link a master meter for this sub-meter.",
      customRender: (field, form, error) => {
        const value = form.watch("linked_master_meter");
        const displayValue = Array.isArray(value) ? value[0] : value || "";
        const isFieldDisabled = isEditing;

        // Find the selected master meter object to show its display label
        const selectedMaster =
          selectedMasterMeterObj ||
          masterMeters?.find((mm: any) => mm.id === Number(displayValue)) ||
          (isEditing &&
          parentMeterDetail &&
          parentMeterDetail.id === Number(displayValue)
            ? {
                id: parentMeterDetail.id,
                meterNumber: parentMeterDetail.meterNumber,
                deviceNo: parentMeterDetail.deviceNo,
                status: parentMeterDetail.status,
                premiseName: parentMeterDetail.premiseName,
              }
            : null);

        const displayLabel = selectedMaster
          ? `${selectedMaster.meterNumber} · ${selectedMaster.deviceNo}`
          : searchMasterMeter;

        return (
          <div className="relative w-full">
            <SearchInput
              ref={masterMeterSearchRef}
              placeholder="Search by meter number, device number..."
              onSearch={handleMasterMeterSearch}
              onSelect={(result) => {
                form.setValue("linked_master_meter", result.id);
                setSelectedMasterMeterObj(result as MasterMeterSearchResult);
              }}
              renderResult={renderMasterMeterResult}
              minSearchLength={3}
              className="w-full"
              debounceMs={200}
              initialValue={displayLabel}
              disabled={isFieldDisabled}
            />
            {(displayValue || searchMasterMeter) && !isFieldDisabled && (
              <button
                type="button"
                className="absolute right-3 top-3 z-20 text-gray-400 hover:text-gray-600"
                onClick={() => {
                  form.setValue("linked_master_meter", "");
                  setSearchMasterMeter("");
                  setSelectedMasterMeterObj(null);
                  masterMeterSearchRef.current?.clearSearch();
                }}
              >
                <X className="h-4 w-4" />
              </button>
            )}

            {/* Selected Master Meter Information Display */}
            {selectedMaster && (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <h4 className="font-medium text-green-800">
                    Selected Master Meter
                  </h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-700">
                      Meter Number:
                    </span>
                    <span className="text-gray-900">
                      {selectedMaster.meterNumber || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-700">
                      Device Number:
                    </span>
                    <span className="text-gray-900">
                      {selectedMaster.deviceNo || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-700">Premise:</span>
                    <span className="text-gray-900">
                      {selectedMaster.premiseName || "N/A"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-gray-700">Status:</span>
                    <span className="text-gray-900">
                      {selectedMaster.status || "N/A"}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>
        );
      },
    },
    {
      name: "category",
      type: "select",
      label: "Category",
      required: true,
      placeholder: "Select category",
      options: dropdownOptions?.categoryConfig,
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Choose the consumer category for billing",
    },
    {
      name: "sub_category",
      type: "select",
      label: "Sub Category",
      required: true,
      placeholder: "Select sub-category",
      options: dropdownOptions?.subCategoryConfig,
      dependsOn: "category",
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      helperText: "Select category first to enable sub-category options",
      groupOrder: 1,
      className: "col-span-2",
    },
    // Manufacturer Details Section
    {
      name: "meter_make",
      type: "select",
      label: "Manufacturer",
      placeholder: "Select manufacturer",
      options: manufacturerOptions,
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      helperText: "Brand or make of the meter",
      className: "col-span-2",
    },
    {
      name: "meter_model",
      type: "select",
      label: "Model",
      placeholder: "Select manufacturer first",
      options: meterModelOptions,
      groupColorNeeded: false,
      group: "Core Meter Information",
      groupHelperText: "Primary meter identity and service configuration.",
      helperText: "Select manufacturer first to enable model options",
      className: "col-span-2",
    },
    // Conditional Sections
    // AMR Details Section
    {
      name: "radio_number",
      type: "text",
      label: "Radio Number",
      placeholder: "Enter radio number",
      required: true,
      group: "AMR Details",
      groupHelperText: "Advanced Meter Reading configuration and parameters.",
      groupColorNeeded: false,
      showWhen: (formValues) =>
        formValues.meter_type === 5 || formValues.meter_type === "5",
      className: "col-span-2",
      helperText: "Unique identifier for the AMR radio",
    },
    {
      name: "read_source",
      type: "select",
      label: "Read Source",
      placeholder: "Select read source",
      options: dropdownOptions?.readSourceOptions,
      group: "AMR Details",
      groupHelperText: "Advanced Meter Reading configuration and parameters.",
      groupColorNeeded: false,
      showWhen: (formValues) =>
        formValues.meter_type === 5 || formValues.meter_type === "5",
      className: "col-span-2",
      helperText: "Source of the meter reading",
    },
    // Prepaid Configuration Section
    {
      name: "current_balance",
      type: "number",
      label: "Current Balance",
      placeholder: "Enter current balance",
      group: "Prepaid Configuration",
      groupHelperText: "Prepaid billing and credit management settings.",
      groupColorNeeded: false,
      showWhen: (formValues) => formValues.meter_type === "Prepaid meter",
      className: "col-span-1",
      helperText: "Current available credit on the meter",
    },
    {
      name: "low_balance_threshold",
      type: "number",
      label: "Low Balance Threshold",
      placeholder: "Enter threshold",
      group: "Prepaid Configuration",
      groupHelperText: "Prepaid billing and credit management settings.",
      groupColorNeeded: false,
      showWhen: (formValues) => formValues.meter_type === "Prepaid meter",
      className: "col-span-1",
      helperText: "Threshold at which a low balance alert is triggered",
    },
    {
      name: "disconnection_enabled",
      type: "select",
      label: "Disconnection Enabled",
      placeholder: "Select yes/no",
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
      group: "Prepaid Configuration",
      groupHelperText: "Prepaid billing and credit management settings.",
      groupColorNeeded: false,
      showWhen: (formValues) => formValues.meter_type === "Prepaid meter",
      className: "col-span-1",
      helperText: "Enable automatic disconnection for low balance",
    },
    {
      name: "disconnection_threshold",
      type: "number",
      label: "Disconnection Threshold",
      placeholder: "Enter threshold",
      group: "Prepaid Configuration",
      groupHelperText: "Prepaid billing and credit management settings.",
      groupColorNeeded: false,
      showWhen: (formValues) =>
        formValues.meter_type === "Prepaid meter" &&
        formValues.disconnection_enabled === "yes",
      className: "col-span-1",
      helperText: "Balance at which the meter will automatically disconnect",
    },
    {
      name: "emergency_credit_limit",
      type: "number",
      label: "Emergency Credit Limit",
      placeholder: "Enter limit",
      group: "Prepaid Configuration",
      groupHelperText: "Prepaid billing and credit management settings.",
      groupColorNeeded: false,
      showWhen: (formValues) => formValues.meter_type === "Prepaid meter",
      className: "col-span-1",
      helperText: "Maximum emergency credit allowed",
    },
    // Smart Meter Config Section
    {
      name: "communication_type",
      type: "select",
      label: "Communication Type",
      placeholder: "Select communication type",
      options: dropdownOptions?.communicationTypeOptions,
      group: "Smart Meter Configuration",
      groupHelperText: "Smart meter connectivity and data management.",
      groupColorNeeded: false,
      showWhen: (formValues) =>
        formValues.meter_type === 1 || formValues.meter_type === "1",
      className: "col-span-2",
      helperText: "Type of communication used by the smart meter",
    },
    {
      name: "sim_module_id",
      type: "text",
      label: "Sim/Module ID",
      placeholder: "Enter SIM or module ID",
      group: "Smart Meter Configuration",
      groupHelperText: "Smart meter connectivity and data management.",
      groupColorNeeded: false,
      showWhen: (formValues) =>
        formValues.meter_type === 1 || formValues.meter_type === "1",
      className: "col-span-2",
      helperText: "Unique identifier for the SIM or communication module",
    },
    {
      name: "hes",
      type: "text",
      label: "HES",
      placeholder: "Enter HES",
      group: "Smart Meter Configuration",
      groupHelperText: "Smart meter connectivity and data management.",
      groupColorNeeded: false,
      showWhen: (formValues) =>
        formValues.meter_type === 1 || formValues.meter_type === "1",
      className: "col-span-2",
      helperText: "Head-End System identifier",
    },

    {
      name: "premise",
      type: "select",
      label: "Premise",
      required: true,
      placeholder: "Select premise",
      options: dropdownOptions.territoryConfig,
      groupColorNeeded: false,
      group: "Location Details",
      groupHelperText: "Define where the meter is installed.",
      fullWidth: true,
      clearable: true,
      groupOrder: 1,
      searchable: true,
      className: "col-span-2",
      helperText: "Premise where the meter will be installed",
    },
    {
      name: "floor",
      type: "text",
      label: "Floor",
      placeholder: "Enter floor number/name",
      groupColorNeeded: false,
      group: "Location Details",
      groupHelperText: "Define where the meter is installed.",
      fullWidth: true,
      maxLength: 50,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Specify floor location of the meter",
    },
    {
      name: "lat",
      type: "negative-decimal",
      label: "Latitude",
      placeholder: "e.g., 40.7128",
      groupColorNeeded: false,
      group: "Location Details",
      groupHelperText: "Define where the meter is installed.",
      required: false,
      fullWidth: true,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Latitude coordinate of meter location",
    },
    {
      name: "long",
      type: "negative-decimal",
      label: "Longitude",
      placeholder: "e.g., -74.0060",
      required: false,
      groupColorNeeded: false,
      group: "Location Details",
      groupHelperText: "Define where the meter is installed.",
      fullWidth: true,
      groupOrder: 1,
      className: "col-span-2",
      helperText: "Longitude coordinate of meter location",
    },
    {
      name: "installed_on",
      type: "datepicker",
      label: "Installation Date",
      required: true,
      disabled: !!isEditing,
      groupColorNeeded: false,
      group: "Installation Details",
      groupHelperText:
        "Installation setup, site coordinates, and service order linkage.",
      fullWidth: true,
      helperText: "Date when the meter was installed",
      groupOrder: 1,
      className: "col-span-2",
    },
    {
      name: "reading_date",
      type: "datepicker",
      label: "Reading Date",
      required: false,
      groupColorNeeded: false,
      group: "Installation Details",
      groupHelperText:
        "Installation setup, site coordinates, and service order linkage.",
      fullWidth: true,
      helperText: "Date when the meter reading was recorded.",
      groupOrder: 1,
      className: "col-span-2",
    },
    {
      name: "initial_reading",
      type: "number",
      label: "Initial Reading",
      required: true,
      groupColorNeeded: false,
      group: "Installation Details",
      groupHelperText:
        "Installation setup, site coordinates, and service order linkage.",
      fullWidth: true,
      helperText: "Enter the non-negative reading captured at installation",
      groupOrder: 1,
      className: "col-span-4",
    },
    {
      name: "is_so_raised",
      type: "radio",
      label: "Is a Service Order Raised?",
      defaultValue: 0,
      options: [
        { label: "Yes", value: 1 },
        { label: "No", value: 0 },
      ],
      group: "Installation Details",
      groupHelperText:
        "Installation setup, site coordinates, and service order linkage.",
      groupColorNeeded: false,
      className: "col-span-4",
    },
    {
      name: "linked_service_order",
      type: "select",
      options: jobCardOptions,
      label: "Search Service Order",
      placeholder: "Search service order",
      group: "Installation Details",
      groupHelperText:
        "Installation setup, site coordinates, and service order linkage.",
      groupColorNeeded: false,
      showWhen: (formValues) => formValues.is_so_raised === "1",
      className: "col-span-4",
      helperText: "Search by service order ID, name, or assigned user",
    },
    {
      name: "so_id",
      type: "autoPopulated",
      label: "SO ID",
      dependsOn: "linked_service_order",
      value: selectedSoCard?.soId ?? "N/A",
      showWhen: (formValues: any) =>
        !!formValues.linked_service_order && formValues.is_so_raised === "1",
      group: "Installation Details",
      groupColorNeeded: false,
      className: "col-span-1 pl-4",
    },
    {
      name: "so_name",
      type: "autoPopulated",
      label: "SO Name",
      dependsOn: "linked_service_order",
      value: selectedSoCard?.soName ?? "N/A",
      showWhen: (formValues: any) =>
        !!formValues.linked_service_order && formValues.is_so_raised === "1",
      group: "Installation Details",
      groupColorNeeded: false,
      className: "col-span-1",
    },
    {
      name: "so_complete_on",
      type: "autoPopulated",
      label: "Completed On",
      dependsOn: "linked_service_order",
      value: selectedSoCard?.completedOn ?? "N/A",
      showWhen: (formValues: any) =>
        !!formValues.linked_service_order && formValues.is_so_raised === "1",
      group: "Installation Details",
      groupColorNeeded: false,
      className: "col-span-1",
    },
    {
      name: "so_assigned_to",
      type: "autoPopulated",
      label: "Assigned To",
      dependsOn: "linked_service_order",
      value: selectedSoCard?.assignedTo ?? "N/A",
      showWhen: (formValues: any) =>
        !!formValues.linked_service_order && formValues.is_so_raised === "1",
      group: "Installation Details",
      groupColorNeeded: false,
      className: "col-span-1",
    },

    // Consumer Assignment Section
    {
      group: "Consumer Assignment",
      groupHelperText: "Assign or unassign a consumer to this meter.",
      groupColorNeeded: false,
      name: "is_assigned",
      disabled: isEditing,
      type: "switch",
      label: "Consumer Assignment",
      defaultValue: assignmentStatus,
      className: "col-span-4",
      helperText: "Toggle on to Assign Consumer, off to Unassign Consumer",
      groupOrder: 2,
    },
    {
      name: "consumer_no",
      type: "custom",
      label: "Search Consumer",
      placeholder: "Search consumer",
      dependsOn: "is_assigned",
      showWhen: (formValues: any) => formValues.is_assigned === true,
      disabled: !!editData?.consumerNo && isEditing,
      required: !isEditing && assignmentStatus,
      group: "Consumer Assignment",
      groupHelperText: "Assign or unassign a consumer to this meter.",
      groupColorNeeded: false,
      className: "col-span-4",
      groupOrder: 2,
      helperText: "Search and assign a consumer to this meter",
      customRender: (field, form, error) => {
        const value = form.watch("consumer_no");
        const displayValue = Array.isArray(value) ? value[0] : value || "";
        const isFieldDisabled = !!editData?.consumerNo && isEditing;

        return (
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search consumer by number..."
                value={searchConsumer || displayValue}
                disabled={isFieldDisabled}
                className="pl-9 pr-9"
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchConsumer(val);
                  setShowConsumerDropdown(true);
                  if (!val) {
                    handleFieldChange("consumer_no", "");
                  }
                }}
                onFocus={() => {
                  if (dropdownOptions.consumersList.length > 0) {
                    setShowConsumerDropdown(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowConsumerDropdown(false), 200);
                }}
              />
              {(displayValue || searchConsumer) && !isFieldDisabled && (
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => {
                    form.setValue("consumer_no", "");
                    setSearchConsumer("");
                    setShowConsumerDropdown(false);
                    handleFieldChange("consumer_no", "");
                  }}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {showConsumerDropdown &&
              searchConsumer &&
              !isFieldDisabled &&
              dropdownOptions.consumersList.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                  {dropdownOptions.consumersList.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 transition-colors"
                      onMouseDown={(e) => {
                        // Prevent Blur from firing before selection
                        e.preventDefault();
                        const val = String(option.value);
                        form.setValue("consumer_no", val);
                        setSearchConsumer(val);
                        setShowConsumerDropdown(false);
                        handleFieldChange("consumer_no", val);
                      }}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
          </div>
        );
      },
    },

    // Consumer Information Display
    {
      groupOrder: 3,
      name: "consumer_name",
      type: "autoPopulated",
      label: "Consumer Name",
      value: profile?.firstName + " " + profile?.lastName,
      dependsOn: "consumer_no",
      group: "Consumer Information",
      groupHelperText:
        "Details of the consumer currently linked to this meter.",
      groupColorNeeded: false,
      showWhen: (formValue: any) =>
        formValue.is_assigned && !!formValue.consumer_no && !!profile,
      className: "col-span-1",
      helperText: "Name of the assigned consumer",
    },
    {
      groupOrder: 3,
      name: "consumer_premise",
      type: "autoPopulated",
      label: "Premise",
      value: profile?.territoryData?.service?.premise,
      dependsOn: "consumer_no",
      group: "Consumer Information",
      groupHelperText:
        "Details of the consumer currently linked to this meter.",
      groupColorNeeded: false,
      showWhen: (formValue: any) =>
        formValue.is_assigned && !!formValue.consumer_no && !!profile,
      className: "col-span-1",
      helperText: "Premise linked to the assigned consumer",
    },
    {
      groupOrder: 3,
      name: "consumer_status",
      type: "autoPopulated",
      label: "Consumer Status",
      value: profile?.statusDisplay,
      dependsOn: "consumer_no",
      group: "Consumer Information",
      groupHelperText:
        "Details of the consumer currently linked to this meter.",
      groupColorNeeded: false,
      showWhen: (formValue: any) =>
        formValue.is_assigned && !!formValue.consumer_no && !!profile,
      className: "col-span-1",
      helperText: "Status of the assigned consumer",
    },
    {
      groupOrder: 3,
      name: "consumer_utility_service",
      type: "autoPopulated",
      label: "Utility Service",
      value: profile?.consumerMappingData?.[0]?.utilityService,
      dependsOn: "consumer_no",
      group: "Consumer Information",
      groupHelperText:
        "Details of the consumer currently linked to this meter.",
      groupColorNeeded: false,
      showWhen: (formValue: any) =>
        formValue.is_assigned && !!formValue.consumer_no && !!profile,
      className: "col-span-1",
      helperText: "Utility service linked to the assigned consumer",
    },

    // Reading Information Section
    {
      group: "Reading Information",
      groupHelperText: "Initial meter reading and its recording date.",
      groupColorNeeded: false,
      name: "current_reading",
      type: "number",
      label: "First Reading",
      placeholder: "Enter initial reading value",
      fullWidth: true,
      disabled: !!editData?.currentReading,
      required: assignmentStatus,
      dependsOn: "is_assigned",
      showWhen: (formValues: any) => formValues.is_assigned === true,
      helperText: "Initial meter reading value",
      groupOrder: 4,
      className: "col-span-2",
    },
    {
      name: "current_reading_date",
      type: "datepicker",
      label: "First Reading Date",
      defaultValue: "08-06-2025",
      group: "Reading Information",
      groupHelperText: "Initial meter reading and its recording date.",
      groupColorNeeded: false,
      required: assignmentStatus,
      fullWidth: true,
      disabled: !!editData?.currentReadingDate,
      dependsOn: "is_assigned",
      showWhen: (formValues: any) => formValues.is_assigned === true,
      helperText: "Date of the first reading",
      groupOrder: 4,
      className: "col-span-2",
    },
  ];

  const formConfig: FormConfig = {
    fields: fields,
    shouldUnregister: true,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      radio: "",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };

  const meterForm = FormService.createForm(formConfig);

  // ===============================
  // VALIDATION FUNCTIONS
  // ===============================
  const validateConsumerApproval = (
    isAssigned: boolean,
    consumerStatus?: string,
  ): boolean => {
    if (!isAssigned) {
      return true;
    }

    // If consumer is assigned, check if status is APPROVED
    if (isAssigned && consumerStatus !== "APPROVED") {
      toast({
        title: "Error",
        description:
          "Consumer status is not approved. Please select an approved consumer.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  // ===============================
  // EVENT HANDLERS
  // ===============================
  const handleFieldChange = (field: string, data: any) => {
    if (field === "is_assigned") {
      setAssignmentStatus(data);
      if (!data) {
        setSearchConsumer("");
        setFieldState((prev) => ({
          ...prev,
          consumerSearch: "",
          current_reading: "",
          current_reading_date: "",
        }));
      }
      return;
    }

    if (field === "consumer_no") {
      const consumerVal = Array.isArray(data) ? data[0] : data;
      if (!consumerVal) {
        setSearchConsumer("");
        setFieldState((prev) => ({ ...prev, consumerSearch: "" }));
      } else {
        setSearchConsumer(consumerVal);
        setFieldState((prev) => ({ ...prev, consumerSearch: consumerVal }));
      }
      return;
    }

    if (field === "link_sub_meters") {
      const val = Array.isArray(data) ? data : [data];
      // val is an array of selected meterNo strings — sync with selectedSubMeters
      setSelectedSubMeters((prev) =>
        prev.filter((sm) => val.includes(sm.meterNo)),
      );
      return;
    }

    if (field === "linked_master_meter") {
      const val = Array.isArray(data) ? data[0] : data;
      if (!val) {
        setSearchMasterMeter("");
        setSelectedMasterMeterObj(null);
      }
      return;
    }

    // Handle meter type changes
    if (field === "meter_type") {
      setMeterType(data);
      return;
    }

    // Handle manufacturer changes
    if (field === "meter_make") {
      setMeterManufacturer(data);
      return;
    }

    if (field === "linked_service_order") {
      const found = serviceOrdersJobCard?.find((jc: any) => jc.soId === data);
      setSelectedSoCard(found ?? null);
      return;
    }

    // Handle dependent field updates
    const fieldUpdateMap: { [key: string]: (data: any) => void } = {
      category: (data) =>
        setFieldState((prev) => ({ ...prev, subCategory: data })),
      product_code: (data) =>
        setFieldState((prev) => ({ ...prev, utility: data })),
      premise: (data) => setFieldState((prev) => ({ ...prev, premise: data })),
      current_reading: (data) =>
        setFieldState((prev) => ({ ...prev, current_reading: data })),
      current_reading_date: (data) =>
        setFieldState((prev) => ({ ...prev, current_reading_date: data })),
    };

    const updateHandler = fieldUpdateMap[field];
    if (updateHandler) {
      updateHandler(data);
    }
  };
  const handleCancel = () => {
    navigate(-1);
  };

  // ===============================
  // UTILITY FUNCTIONS
  // ===============================

  const cleanPayload = (data: any) => {
    const cleaned: any = {};

    // Auto-populated display-only fields that should not be sent to the API
    const excludeFields = [
      "consumer_name",
      "consumer_premise",
      "consumer_status",
      "consumer_utility_service",
    ];

    Object.keys(data).forEach((key) => {
      if (excludeFields.includes(key)) return;
      const value = data[key];
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isMeaningful =
        value !== null &&
        value !== undefined &&
        value !== "" &&
        value !== "null" &&
        value !== "undefined" &&
        !isEmptyArray;

      if (isMeaningful) {
        if (
          key === "consumerNo" ||
          (key === "consumer_no" && Array.isArray(value) && value.length === 1)
        ) {
          cleaned[key] = value[0];
        } else {
          cleaned[key] = value;
        }
      }
    });

    return cleaned;
  };

  const prepareFormData = (data: any) => {
    if (data.meterMake && manufacturers) {
      const selectedManufacturer = manufacturers.find(
        (m: any) => m.id === data.meterMake,
      );
      console.log("selectedManufacturer", selectedManufacturer);
      if (selectedManufacturer) {
        data.meterMake = selectedManufacturer.id;
      }
    }

    return data;
  };

  // ===============================
  // CLASSIFICATION / METER-TYPE PAYLOAD BUILDER
  // ===============================
  /**
   * Appends classification-based and meter-type-based fields to the payload.
   * - ownership  (0 = Master, 1 = Individual, 2 = Sub-meter)
   * - master_meter_role_type + linked_sub_meters  (Master only)
   * - parent_meter  (Sub-meter only)
   * - service_order_id  (when SO is raised)
   * - additional_data  (Smart meter → meter_type_data, AMR → meter_type_data or amr_details)
   */
  const appendClassificationPayload = (formData: any, data: any) => {
    // ── ownership ──────────────────────────────────────────────────────
    const classificationRaw = data.meter_classification;
    const classificationNum =
      classificationRaw !== undefined && classificationRaw !== ""
        ? Number(classificationRaw)
        : undefined;

    if (classificationNum !== undefined) {
      formData.ownership = classificationNum; // 0=Master, 1=Individual, 2=Sub-meter
    }

    // ── Master meter: role type + linked sub-meters ────────────────────
    if (classificationNum === 0) {
      if (
        data.master_meter_role_type !== undefined &&
        data.master_meter_role_type !== ""
      ) {
        formData.master_meter_role_type = Number(data.master_meter_role_type);
      }
    }

    // ── Sub-meter: parent meter ────────────────────────────────────────
    if (classificationNum === 2) {
      const masterMeterVal = data.linked_master_meter;
      const parentId = Array.isArray(masterMeterVal)
        ? masterMeterVal[0]
        : masterMeterVal;
      if (parentId !== undefined && parentId !== "") {
        formData.parent_meter = Number(parentId);
      }
      const subMeters = Array.isArray(data.linked_sub_meters)
        ? data.linked_sub_meters
        : [];
      if (subMeters.length > 0) {
        formData.linked_sub_meters = subMeters;
      }
    }

    // ── Service order linkage ──────────────────────────────────────────
    if (data.is_so_raised === "1" && data.linked_service_order) {
      formData.service_order_id = data.linked_service_order;
    }

    // ── additional_data by meter type ──────────────────────────────────
    const meterTypeVal = data.meter_type;

    // Smart meter (meter_type === 1)
    if (Number(meterTypeVal) === 1) {
      const smartData: Record<string, any> = {};
      if (
        data.communication_type !== undefined &&
        data.communication_type !== ""
      )
        smartData.communication_type = data.communication_type;
      if (data.sim_module_id !== undefined && data.sim_module_id !== "")
        smartData.sim_module_id = data.sim_module_id;
      if (data.hes !== undefined && data.hes !== "") smartData.hes = data.hes;
      if (Object.keys(smartData).length > 0) {
        formData.additional_data = { meter_type_data: smartData };
      }
    }

    // AMR meter (meter_type === 5)
    if (Number(meterTypeVal) === 5) {
      const amrData: Record<string, any> = {};
      if (data.radio_number !== undefined && data.radio_number !== "")
        amrData.radio_number = data.radio_number;
      if (data.read_source !== undefined && data.read_source !== "")
        amrData.read_source = data.read_source;
      if (Object.keys(amrData).length > 0) {
        formData.additional_data = { meter_type_data: amrData };
      }
    }
  };

  const handleSubmitForm = async (data: any) => {
    try {
      if (!meterNo?.meterNumber && !isEditing) {
        toast({
          title: "Error",
          description:
            "Meter number is not generated yet. Please wait and try again.",
          variant: "destructive",
        });

        return;
      }

      data = prepareFormData(data);
      let formData = FormService.createPayload(data, fields);
      formData = cleanPayload(formData);

      if (isEditing) {
        const convertedEditData = convertToSnakeCase(editData);
        let editPayload = FormService.getUpdatedFields(convertedEditData, data);
        editPayload = cleanPayload(editPayload);
        if (editData?.consumerNo) {
          delete editPayload.is_assigned;
        }

        if (data.is_assigned === false) {
          delete editPayload.installed_on;
          delete editPayload.current_reading;
          delete editPayload.current_reading_date;
          editPayload.status = 0;
          editPayload.is_assigned = false;
          if (editData?.consumerNo) {
            editPayload.consumer_no = editData.consumerNo;
          }
        } else {
          editPayload;
        }
        patchDeviceMutation.mutate(
          { deviceId: id, payload: editPayload },
          {
            onSuccess: () => {
              navigate(`/mx/device-management/meter/view/${id}?tab=readings`);
              queryClient.refetchQueries({
                queryKey: QueryKeyFactory.module.mx.logs
                  .activityLogs(id)
                  .slice(0, 2),
              });
            },
            onError: (error) => {
              console.error("Edit error:", error);
              toast({
                title: "Error",
                description: "Failed to update meter. Please try again.",
                variant: "destructive",
              });
            },
          },
        );
      } else {
        if (
          !validateConsumerApproval(data.is_assigned, profile?.statusDisplay)
        ) {
          return;
        }
        const requiredFields = {
          meter_number: meterNo?.meterNumber || "",
          remote_utility_id: remoteUtilityId,
          status: assignmentStatus ? 1 : 0,
          physical_status: 0,
        };

        Object.assign(formData, requiredFields);

        if (assignmentStatus) {
          formData.reading_status = 0;
        }

        // ── Append classification / meter-type specific payload ──────────
        appendClassificationPayload(formData, data);

        if (!formData.meter_number) {
          toast({
            title: "Error",
            description: "Unable to generate meter number. Please try again.",
            variant: "destructive",
          });
          return;
        }
        console.log("debug addd", formData);
        await createDevice(formData, {
          onSuccess: () => {
            logEvent("New Meter added", {
              ...formData,
            });
            queryClient.refetchQueries({
              queryKey: ["mx", "list"],
            });
          },
          onError: () => {
            logEvent("New Meter Addition Failed", {
              ...formData,
            });
          },
        });
        toast({
          title: "Success",
          description: "Meter created successfully",
          variant: "default",
        });
        navigate("/mx/device-management/meter", { replace: true });
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast({
        title: "Error",
        description: "An error occurred while saving the meter",
        variant: "destructive",
      });
    }
  };
  const handleNextSubmit = async (data: any) => {
    try {
      // Validate consumer approval status before proceeding
      if (!validateConsumerApproval(data.is_assigned, profile?.statusDisplay)) {
        return;
      }

      if (!meterNo?.meterNumber) {
        toast({
          title: "Error",
          description:
            "Meter number is not generated yet. Please wait and try again.",
          variant: "destructive",
        });

        return;
      }

      data = prepareFormData(data);
      let formData = FormService.createPayload(data, fields);
      formData = cleanPayload(formData);
      delete formData.is_assigned;

      const requiredFields = {
        meter_number: meterNo?.meterNumber,
        remote_utility_id: remoteUtilityId,
        status: assignmentStatus ? 1 : 0,
        physical_status: 0,
      };

      Object.assign(formData, requiredFields);

      if (assignmentStatus) {
        formData.reading_status = 0;
      }

      // ── Append classification / meter-type specific payload ────────────
      appendClassificationPayload(formData, data);

      await createDevice(formData, {
        onSuccess: () => {
          logEvent("New Meter added", {
            ...formData,
          });
          toast({
            title: "Success",
            description: "Meter added successfully",
          });
          queryClient.refetchQueries({
            queryKey: ["mx", "list"],
          });
        },
        onError: () => {
          logEvent("New Meter Addition Failed", {
            ...formData,
          });
        },
      });
      FormService.resetForm(meterForm, fields);
      setAssignmentStatus(false);
      setSearchConsumer("");
      setFieldState({
        subCategory: null,
        premise: null,
        utility: null,
        manufacturer: null,
        consumerSearch: "",
      });
    } catch (error) {
      console.error("Next submit error:", error);
      toast({
        title: "Error",
        description: "An error occurred while saving the meter",
        variant: "destructive",
      });
    }
  };

  // ===============================
  // EFFECTS FOR DATA LOADING
  // ===============================

  // Initialize field states early when edit data is available
  useEffect(() => {
    if (isEditing && editData && Object.keys(editData).length > 0) {
      // Set field states for dependent dropdowns immediately
      setFieldState((prev) => ({
        ...prev,
        consumerSearch: editData.consumerNo || "",
        utility: editData.productCode || null,
        premise: editData.premise || null,
        subCategory: editData.category || null,
      }));

      // Set other states immediately
      if (editData.meterType) {
        setMeterType(editData.meterType.toString());
      }

      if (editData.meterMake) {
        setMeterManufacturer(editData.meterMake.toString());
      }

      if (editData.consumerNo) {
        setAssignmentStatus(true);
        setSearchConsumer(editData.consumerNo);
      }

      if (editData.parentMeter) {
        setSearchMasterMeter(
          editData.masterMeter || String(editData.parentMeter),
        );
      }

      setDisplayMeterNo(editData.meterNumber);
    }
  }, [isEditing, editData]);

  // Populate form data when all dependencies are loaded
  useEffect(() => {
    if (
      isEditing &&
      editData &&
      Object.keys(editData).length > 0 &&
      !isEditDataLoaded &&
      // Wait for essential dropdown data to be loaded
      categoryConfig &&
      territoryConfig &&
      manufacturers &&
      meterTypes
    ) {
      const convertedEditData = convertToSnakeCase(editData);
      console.log("Converted Edit Data:", convertedEditData);

      // Populate form with edit data
      FormService.populateForm(meterForm, convertedEditData, fields);

      // Set form values for dependent fields
      if (editData.category) {
        meterForm.setValue("category", editData.category);
      }
      if (editData.productCode) {
        meterForm.setValue("product_code", editData.productCode);
      }
      if (editData.premise) {
        meterForm.setValue("premise", editData.premise);
      }
      if (editData.meterType) {
        meterForm.setValue("meter_type", editData.meterType);
      }

      // Handle manufacturer selection
      if (editData.meterMake && manufacturers) {
        const selectedManufacturer = manufacturers.find(
          (m: any) =>
            m.name === editData.meterMake || m.id === editData.meterMake,
        );

        if (selectedManufacturer) {
          meterForm.setValue("meter_make", selectedManufacturer.id);
        }
      }

      // Handle consumer assignment
      if (editData.consumerNo) {
        meterForm.setValue("is_assigned", true);
        meterForm.setValue("consumer_no", editData.consumerNo);
      }

      // Handle parent meter assignment
      if (editData.parentMeter) {
        meterForm.setValue("linked_master_meter", editData.parentMeter);
      }

      // Mark edit data as loaded
      setIsEditDataLoaded(true);
    }
  }, [
    isEditing,
    editData,
    manufacturers,
    meterForm,
    isEditDataLoaded,
    categoryConfig,
    territoryConfig,
    meterTypes,
    fields,
  ]);

  // Handle sub-category population when sub-category config is loaded
  useEffect(() => {
    if (
      isEditing &&
      editData &&
      isEditDataLoaded &&
      editData.subCategory &&
      subCategoryConfig &&
      subCategoryConfig.length > 0
    ) {
      meterForm.setValue("sub_category", editData.subCategory);
    }
  }, [isEditing, editData, isEditDataLoaded, subCategoryConfig, meterForm]);

  // Handle meter model population when meter models are loaded
  useEffect(() => {
    if (
      isEditing &&
      editData &&
      isEditDataLoaded &&
      editData.meterModel &&
      meterModels &&
      meterModels.length > 0
    ) {
      meterForm.setValue("meter_model", editData.meterModel);
    }
  }, [isEditing, editData, isEditDataLoaded, meterModels, meterForm]);

  const renderMeterStatusBadges = () => (
    <div className="bg-muted/20 p-4 rounded-lg border-2 border-muted mb-4">
      <h3 className="font-medium text-lg mb-3">Meter Status</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">Physical Status</label>
          <Badge
            variant="outline"
            className="w-full py-2 justify-center bg-green-50"
          >
            <CheckCircle className="h-4 w-4 mr-2 text-green-500" />
            Installed
          </Badge>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Assignment Status</label>
          <Badge
            variant="outline"
            className="w-full py-2 justify-center bg-blue-50"
          >
            <UserCheck className="h-4 w-4 mr-2 text-blue-500" />
            {assignmentStatus ? "Assigned" : "Unassigned"}
          </Badge>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">Reading Status</label>
          <Badge
            variant="outline"
            className="w-full py-2 justify-center bg-yellow-50"
          >
            <AlertCircle className="h-4 w-4 mr-2 text-yellow-500" />
            Normal
          </Badge>
        </div>
      </div>
    </div>
  );

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 pt-6 border-t">
      <button
        type="button"
        onClick={handleCancel}
        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={meterForm.handleSubmit(handleSubmitForm)}
        disabled={!isEditing && meterNoLoading}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {!isEditing && meterNoLoading
          ? "Generating..."
          : isEditing
            ? "Update Meter"
            : "Add Meter"}
      </button>
      {!isEditing && (
        <button
          type="button"
          onClick={meterForm.handleSubmit(handleNextSubmit)}
          disabled={meterNoLoading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {meterNoLoading ? "Generating..." : "Add & Next"}
        </button>
      )}
    </div>
  );

  return (
    <div className="page-container">
      <div className="w-full">
        {/* Header */}
        <div className="mb-4 flex items-center">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to meters
          </Button>
        </div>

        {/* Main Card */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center p-4">
              <div>
                <CardTitle className="page-subheading flex items-center gap-2">
                  {isEditing ? "Edit Meter" : "Add New Meter"}
                  <CustomTooltip
                    message={`${
                      !isEditing
                        ? "Fill out this form to register a new meter with all required details."
                        : "Update the meter details. Ensure the information is accurate before saving."
                    }`}
                  />
                </CardTitle>
                <CardDescription className="card-subheading-text">
                  {isEditing
                    ? "Update meter details"
                    : "Fill in the details to add a new meter"}
                </CardDescription>
              </div>
              <Badge variant="outline" className="text-base text-neutral-700">
                <span>{`#${String(
                  isEditing
                    ? displayMeterNo
                    : meterNo?.meterNumber || "Generating...",
                )}`}</span>
              </Badge>
            </div>
          </CardHeader>

          <CardContent>
            {/* Meter Status Section */}
            {renderMeterStatusBadges()}

            {/* Dynamic Form */}
            <DynamicForm
              form={meterForm}
              config={formConfig}
              fields={fields}
              onSubmit={handleSubmitForm}
              gridColumns={4}
              onFieldChange={handleFieldChange}
              renderCustomFooter={renderCustomFooter}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddEditMeter;
